import { useTranslation } from 'next-i18next'

import { useAppDispatch, useAppSelector } from '~/redux/hooks'

import { CurriculumEvents } from '~/analytics/curriculum/curriculumEvents.types'
import curriculumAnalytics from '~/analytics/curriculum/curriculumAnalytics'

import ArrowButton from '~/checklist/components/ArrowButton'

import { useGetInCurriculumV2 } from '../hooks/useGetCurriculumCountryCode'
import { getCurriculumDisplayLabel } from '../constants/usCurriculumStates.const'

import {
  selectMenuCurriculumDropdownExpanded,
  setMenuCurriculumDropdownExpandedActionCreator,
} from '../slice/curriculum.slice'

import CurriculumDropdownItem from './CurriculumDropdownItem'
import CurriculumDropdownOptions from './CurriculumDropdownOptions'

interface Props {
  currentCurriculum: string
  currentCurriculumCode: string
  unsetStateLabel?: string
  handleOptionSelect: (chosenCurriculumName: string) => void
  theme?: 'light' | 'dark'
  isCampaignBar?: boolean
}

export default function MenuCurriculumMobileDropdown({
  currentCurriculum,
  currentCurriculumCode,
  unsetStateLabel,
  handleOptionSelect,
  theme = 'light',
  isCampaignBar,
}: Props): React.ReactElement {
  const isCurriculumV2 = useGetInCurriculumV2()
  const dispatch = useAppDispatch()
  const isExpanded = useAppSelector(selectMenuCurriculumDropdownExpanded)
  const { t } = useTranslation('curriculum')
  const isDark = theme === 'dark'
  const currentCurriculumTitle = isDark
    ? unsetStateLabel ?? getCurriculumDisplayLabel(currentCurriculum)
    : `${t('dropdownTitle')}: ${unsetStateLabel ?? currentCurriculum}`
  const lightTriggerChrome = isCampaignBar ? 'pr-8 text-white hover:bg-purple-darker' : 'hover:bg-purple-medium'
  const arrowColor = isDark || isCampaignBar ? 'text-white' : 'text-black'
  return (
    <div className={`w-auto font-medium ${isDark ? 'text-white' : 'ml-5 mr-2 text-gray-dashboard-header'}`}>
      {isDark && (
        <p className="px-1.5 pb-1 text-2.5 font-bold uppercase tracking-half-px text-white/50">{t('dropdownTitle')}</p>
      )}
      <div
        className={`relative pl-1.5 w-full h-9 flex items-center space-x-4 rounded-1 ${
          isDark ? 'hover:bg-purple-darker' : lightTriggerChrome
        } duration-300 cursor-pointer`}
        onClick={() => {
          const nextExpanded = !isExpanded
          curriculumAnalytics.logCurriculumClick(
            CurriculumEvents.CURRICULUM_COUNTRY_SELECTOR_TOGGLED,
            { surface: 'menu', is_expanded: nextExpanded },
            isCurriculumV2 ? 'v2' : 'v1',
          )
          dispatch(setMenuCurriculumDropdownExpandedActionCreator(nextExpanded))
        }}
      >
        <CurriculumDropdownItem
          curriculumCountryCode={currentCurriculumCode}
          curriculumName={currentCurriculumTitle}
          isDark={isDark}
          isMenu
          isUnset={Boolean(unsetStateLabel)}
          textClassName={isDark ? 'text-xs font-normal text-white-strong' : undefined}
        />
        <div className="absolute right-2 flex justify-center items-center">
          <ArrowButton color={arrowColor} expanded={isExpanded} size={17} />
        </div>
      </div>
      <div
        className={`z-20 overflow-hidden w-full ${isDark ? 'bg-transparent text-white' : 'pl-6.5 bg-white'} rounded-2`}
        style={{
          transition: `max-height 400ms cubic-bezier( 0.4, 0, 1, 1)`,
          maxHeight: isExpanded ? (isDark ? '550px' : '200px') : 0,
        }}
      >
        <CurriculumDropdownOptions
          currentCurriculum={currentCurriculum}
          currentCurriculumCode={currentCurriculumCode}
          handleOptionSelect={handleOptionSelect}
          isDark={isDark}
          isMenu
        />
      </div>
    </div>
  )
}
